import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { hitApi, commonToast } from "../../../components/commonAction";
import BillingTabs from "./BillingTabs";
import "../../../styles/admin-drawer.css";

const EMPTY_FORM = {
    code: "",
    name: "",
    description: "",
    billingType: "one_time",
    billingInterval: "",
    price: 0,
    currency: "USD",
    displayOrder: 0,
    isActive: true,
};

export default function Tiers({ pageTitle }) {
    const [tiers, setTiers] = useState([]);
    const [products, setProducts] = useState([]);     // each with embedded plans
    const [tierBundles, setTierBundles] = useState({}); // tierId -> array of {product, plan}
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editingId, setEditingId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const [bundleEditorTier, setBundleEditorTier] = useState(null);
    const [bundleSelections, setBundleSelections] = useState({}); // productId -> productPlanId
    const [bundleSaving, setBundleSaving] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const openCreate = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setDrawerOpen(true);
    };
    const closeDrawer = () => setDrawerOpen(false);

    useEffect(() => {
        if (drawerOpen) return;
        const t = setTimeout(() => {
            setEditingId(null);
            setForm(EMPTY_FORM);
        }, 250);
        return () => clearTimeout(t);
    }, [drawerOpen]);

    useEffect(() => {
        if (!drawerOpen) return;
        const onKey = (e) => { if (e.key === 'Escape') closeDrawer(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [drawerOpen]);

    const loadAll = () => {
        setLoading(true);
        // admin tier list (incl. inactive)
        hitApi({ url: "admin/billing/tiers", method: "GET", data: {}, alert: false }, (resp) => {
            if (resp?.status) setTiers(resp.data?.tiers || []);
        });
        // public catalog: products with plans (used for the bundle editor) + tier bundles
        hitApi({ url: "billing/products", method: "GET", data: {}, alert: false }, (resp) => {
            if (resp?.status) setProducts(resp.data?.products || []);
        });
        hitApi({ url: "billing/tiers", method: "GET", data: {}, alert: false }, (resp) => {
            setLoading(false);
            if (resp?.status) {
                const map = {};
                (resp.data?.tiers || []).forEach(t => { map[t._id] = t.plans || []; });
                setTierBundles(map);
            }
        });
    };
    useEffect(loadAll, []);

    const startEdit = (t) => {
        setEditingId(t._id);
        setForm({
            code: t.code,
            name: t.name,
            description: t.description || "",
            billingType: t.billingType,
            billingInterval: t.billingInterval || "",
            price: t.price,
            currency: t.currency || "USD",
            displayOrder: t.displayOrder || 0,
            isActive: !!t.isActive,
        });
        setDrawerOpen(true);
    };
    const cancelEdit = () => { closeDrawer(); };

    const submitTier = (e) => {
        e.preventDefault();
        if (!form.code.trim() || !form.name.trim()) {
            commonToast("error", "Code and name are required.");
            return;
        }
        setSubmitting(true);
        const billingInterval = form.billingType === "recurring" ? (form.billingInterval || "monthly") : null;
        const body = { ...form, billingInterval, price: Number(form.price) || 0, displayOrder: Number(form.displayOrder) || 0 };

        const url = editingId ? `admin/billing/tiers/${editingId}` : `admin/billing/tiers`;
        const method = editingId ? "PATCH" : "POST";
        const sendBody = editingId
            ? {
                name: body.name, description: body.description, price: body.price, currency: body.currency,
                displayOrder: body.displayOrder, isActive: body.isActive, billingInterval: body.billingInterval,
            }
            : body;

        hitApi({ url, method, data: sendBody }, (resp) => {
            setSubmitting(false);
            if (resp?.status) {
                commonToast("success", resp.message);
                closeDrawer();
                loadAll();
            }
        });
    };

    /* ---------------- bundle editor ----------------- */

    const openBundleEditor = (tier) => {
        setBundleEditorTier(tier);
        // pre-select existing bundle items
        const existing = tierBundles[tier._id] || [];
        const sel = {};
        existing.forEach(b => {
            if (b.product?._id && b.plan?._id) sel[String(b.product._id)] = String(b.plan._id);
        });
        setBundleSelections(sel);
    };
    const closeBundleEditor = () => { setBundleEditorTier(null); setBundleSelections({}); };

    const setProductSelection = (productId, planId) => {
        setBundleSelections(prev => {
            const next = { ...prev };
            if (planId) next[productId] = planId; else delete next[productId];
            return next;
        });
    };

    const saveBundle = () => {
        if (!bundleEditorTier) return;
        const items = Object.entries(bundleSelections).map(([productId, productPlanId]) => ({ productId, productPlanId }));
        setBundleSaving(true);
        hitApi(
            { url: `admin/billing/tiers/${bundleEditorTier._id}/plans`, method: "PUT", data: { items } },
            (resp) => {
                setBundleSaving(false);
                if (resp?.status) {
                    commonToast("success", resp.message);
                    closeBundleEditor();
                    loadAll();
                }
            }
        );
    };

    return (
        <div className="pxl_rightBar_assets">
            <Helmet><title>{pageTitle}</title></Helmet>
            <div className="pxl-rb-header-wrap"><div className="pxl-headingTop"><h2 className="pxl-rb-header-heading">Billing</h2></div></div>
            <BillingTabs />

            <div className="row g-3">
                <div className="col-12">
                    <div className="pxl-panel">
                        <div className="pxl-panel-header">
                            <span>Tiers ({tiers.length})</span>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="btn btn-sm btn-light" onClick={loadAll}>Refresh</button>
                                <button className="pxl-drawer-new-btn" onClick={openCreate}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                                    </svg>
                                    New Tier
                                </button>
                            </div>
                        </div>
                        <div className="pxl-panel-body pxl-panel-body--flush">
                            {loading ? (
                                <div className="text-muted" style={{ padding: 24, textAlign: "center" }}>Loading…</div>
                            ) : tiers.length === 0 ? (
                                <div className="text-muted" style={{ padding: 24, textAlign: "center" }}>No tiers yet. Create up to 3 on the left.</div>
                            ) : (
                                tiers.map(t => (
                                    <div key={t._id} style={{ borderBottom: "1px solid #e3e7f0", padding: 16 }}>
                                        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                <h5 style={{ margin: "0 0 4px", fontSize: 16, color: "#1f2238" }}>
                                                    {t.name}{" "}
                                                    <small style={{ color: "#8a8fa6", fontWeight: 400, fontSize: 12 }}>
                                                        <code>{t.code}</code> · {t.currency} {Number(t.price).toFixed(2)} ·
                                                        {" "}{t.billingType}{t.billingInterval ? `/${t.billingInterval}` : ""}
                                                    </small>
                                                </h5>
                                                {t.description && <div style={{ color: "#8a8fa6", fontSize: 13 }}>{t.description}</div>}
                                            </div>
                                            <div className="d-flex flex-shrink-0 align-items-center gap-1">
                                                {!t.isActive && <span className="pxl-pill pxl-pill--muted" style={{ marginRight: 8 }}>inactive</span>}
                                                <button className="btn btn-sm btn-link" onClick={() => startEdit(t)}>Edit tier</button>
                                                <button className="btn btn-sm btn-link" onClick={() => openBundleEditor(t)}>Edit bundle</button>
                                            </div>
                                        </div>
                                        <div style={{ marginTop: 8, fontSize: 13 }}>
                                            <strong style={{ marginRight: 8, color: "#4f536f" }}>Bundle:</strong>
                                            {(tierBundles[t._id] || []).length === 0 ? (
                                                <span style={{ color: "#8a8fa6" }}>no products attached</span>
                                            ) : (
                                                (tierBundles[t._id] || []).map((b, i) => (
                                                    <span key={i} className="pxl-pill pxl-pill--muted" style={{ marginRight: 4 }}>
                                                        {b.product?.name || "?"} · {b.plan?.name || "?"}
                                                    </span>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Right-side drawer: create / edit tier ── */}
            <div
                className={`pxl-drawer-backdrop${drawerOpen ? ' is-open' : ''}`}
                onClick={closeDrawer}
            />
            <aside
                className={`pxl-drawer${drawerOpen ? ' is-open' : ''}`}
                role="dialog"
                aria-modal="true"
                aria-label={editingId ? 'Edit tier' : 'New tier'}
            >
                <div className="pxl-drawer-header">
                    <div>
                        <h3 className="pxl-drawer-title">{editingId ? 'Edit Tier' : 'New Tier'}</h3>
                        <p className="pxl-drawer-sub">
                            {editingId
                                ? `Code: ${form.code}`
                                : 'Add a new tier to the public pricing page.'}
                        </p>
                    </div>
                    <button
                        type="button"
                        className="pxl-drawer-close"
                        onClick={closeDrawer}
                        title="Close"
                        aria-label="Close"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>

                <form onSubmit={submitTier} className="pxl-drawer-body" id="tierForm">
                    <div className="pxl_input_feilds pxl-input-compact">
                        <label>Code</label>
                        <input value={form.code}
                            onChange={e => setForm({ ...form, code: e.target.value })}
                            disabled={!!editingId} placeholder="tier1 / tier2 / tier3" />
                    </div>
                    <div className="pxl_input_feilds pxl-input-compact">
                        <label>Name</label>
                        <input value={form.name}
                            onChange={e => setForm({ ...form, name: e.target.value })} />
                    </div>
                    <div className="pxl_input_feilds pxl-input-compact">
                        <label>Description</label>
                        <textarea rows={2} value={form.description}
                            onChange={e => setForm({ ...form, description: e.target.value })} />
                    </div>
                    <div className="row">
                        <div className="col-7">
                            <div className="pxl_input_feilds pxl-input-compact">
                                <label>Billing</label>
                                <select value={form.billingType}
                                    onChange={e => setForm({ ...form, billingType: e.target.value })}
                                    disabled={!!editingId}>
                                    <option value="one_time">One-time</option>
                                    <option value="recurring">Recurring</option>
                                </select>
                            </div>
                        </div>
                        <div className="col-5">
                            <div className="pxl_input_feilds pxl-input-compact">
                                <label>Interval</label>
                                <select value={form.billingInterval}
                                    onChange={e => setForm({ ...form, billingInterval: e.target.value })}
                                    disabled={form.billingType === "one_time"}>
                                    <option value="">—</option>
                                    <option value="monthly">Monthly</option>
                                    <option value="yearly">Yearly</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-7">
                            <div className="pxl_input_feilds pxl-input-compact">
                                <label>Price</label>
                                <input type="number" step="0.01" value={form.price}
                                    onChange={e => setForm({ ...form, price: e.target.value })} />
                            </div>
                        </div>
                        <div className="col-5">
                            <div className="pxl_input_feilds pxl-input-compact">
                                <label>Currency</label>
                                <input value={form.currency}
                                    onChange={e => setForm({ ...form, currency: e.target.value })} />
                            </div>
                        </div>
                    </div>
                    <div className="pxl_input_feilds pxl-input-compact">
                        <label>Display order</label>
                        <input type="number" value={form.displayOrder}
                            onChange={e => setForm({ ...form, displayOrder: e.target.value })} />
                    </div>
                    <div className="form-check mb-3">
                        <input type="checkbox" className="form-check-input" id="tierActive"
                            checked={form.isActive}
                            onChange={e => setForm({ ...form, isActive: e.target.checked })} />
                        <label className="form-check-label" htmlFor="tierActive">Active</label>
                    </div>
                </form>

                <div className="pxl-drawer-footer">
                    <button type="submit" form="tierForm" className="pxlCommon-btn" disabled={submitting}>
                        {submitting ? (editingId ? 'Saving…' : 'Creating…') : (editingId ? 'Save' : 'Create')}
                    </button>
                    <button type="button" className="btn btn-light" onClick={cancelEdit}>Cancel</button>
                </div>
            </aside>

            {bundleEditorTier && (
                <div className="modal d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Edit bundle — {bundleEditorTier.name}</h5>
                                <button className="btn-close" onClick={closeBundleEditor}></button>
                            </div>
                            <div className="modal-body">
                                <p className="text-muted">
                                    Pick at most one plan per product. Leaving "none" excludes that product from this tier.
                                </p>
                                {products.length === 0 ? (
                                    <div className="alert alert-warning">No active products with plans yet. Add some on the Plans tab first.</div>
                                ) : (
                                    <table className="table table-sm">
                                        <thead><tr><th>Product</th><th>Plan to grant</th></tr></thead>
                                        <tbody>
                                            {products.map(p => (
                                                <tr key={p._id}>
                                                    <td>{p.name}</td>
                                                    <td>
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={bundleSelections[p._id] || ""}
                                                            onChange={e => setProductSelection(p._id, e.target.value)}
                                                        >
                                                            <option value="">— none —</option>
                                                            {(p.plans || []).map(pl => (
                                                                <option key={pl._id} value={pl._id}>
                                                                    {pl.name} ({pl.currency} {Number(pl.price).toFixed(2)})
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={closeBundleEditor} disabled={bundleSaving}>Cancel</button>
                                <button className="btn btn-primary" onClick={saveBundle} disabled={bundleSaving}>
                                    {bundleSaving ? "Saving…" : "Save bundle"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
